import React, { useState, useEffect } from 'react';
import { Button, Popconfirm, Input, Spin, Form } from '@myua/kit';
import { AddSquare, TickCircle } from '@myua/icons/dist/bulk14';
import { Edit2, Trash } from '@myua/icons/dist/bulk16';
import { useDispatch, useSelector } from 'react-redux';
import * as assetLinkActions from '../actions';
import { getDetails } from '../../finder/selectors';
import '../styles.scss';
import { Asset, AssetLink } from '../../../api';
import { getInProgress } from '../selectors';
import { getPermissions } from '../../app/selectors';
import { Permission } from '../../../../general/model';

const MAX_LINKS_NUMBER = 5;

export const AssetLinks = () => {
  const dispatch = useDispatch();
  const details = useSelector(getDetails).asset;
  const inProgress = useSelector(getInProgress);
  const permissions = useSelector(getPermissions);
  const hasPermission = permissions[Permission['fs.assetlab_asset_links']];

  const [activeId, setActiveId] = useState<
    // number - id редактируемой ссылки, undefined - для создания новой ссылки, null - когда ничего не происходит
    number | undefined | null
  >(null);

  const onClose = () => {
    setActiveId(null);
  };

  if (!details) {
    return null;
  }

  if (!hasPermission && details.links.length === 0) {
    return null;
  }

  let adding: React.ReactNode = null;
  if (hasPermission) {
    if (activeId === undefined) {
      adding = <LinkInput assetId={details.id} linkId={activeId} onClose={onClose} />;
    } else if (details.links.length < MAX_LINKS_NUMBER) {
      adding = (
        <Button
          size="small"
          icon={<AddSquare />}
          onClick={() => {
            setActiveId(undefined);
          }}
        >
          Add link
        </Button>
      );
    }
  }

  return (
    <div className="al-asset-links al-btn-blue-icon">
      <Spin spinning={inProgress}>
        {adding && <div className="al-asset-links__adding">{adding}</div>}

        <div className="al-asset-links__links">
          {details.links.map((link) => (
            <div key={link.id} className="al-asset-link">
              {link.id === activeId ? (
                <LinkInput
                  assetId={details.id}
                  linkId={activeId}
                  onClose={onClose}
                  initialValue={link.url}
                />
              ) : (
                <>
                  <a
                    href={link.url}
                    title={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="al-asset-link__title"
                  >
                    {link.url}
                  </a>
                  <div className="al-asset-link__space" />
                  {hasPermission && link.editable ? (
                    <>
                      <Button
                        icon={<Edit2 />}
                        type="text"
                        size="small"
                        className="al-asset-link__button"
                        onClick={() => {
                          setActiveId(link.id);
                        }}
                      />
                      <Popconfirm
                        getPopupContainer={() =>
                          document.querySelector('.al-finder-details__main') || document.body
                        }
                        align={{ offset: [14, 8] }}
                        title="Delete the link?"
                        onConfirm={() =>
                          dispatch(
                            assetLinkActions.deleteConfirmed({
                              assetId: details.id,
                              linkId: link.id,
                            }),
                          )
                        }
                        okText="Delete"
                        placement="topRight"
                        showArrow={true}
                      >
                        <Button
                          icon={<Trash />}
                          type="text"
                          size="small"
                          className="al-asset-link__button"
                        />
                      </Popconfirm>
                    </>
                  ) : null}
                </>
              )}
            </div>
          ))}
        </div>
      </Spin>
    </div>
  );
};

const LinkInput: React.FC<{
  assetId: Asset['id'];
  linkId: AssetLink['id'] | undefined;
  initialValue?: string;
  onClose: () => void;
}> = ({ assetId, linkId, initialValue, onClose }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const FIELD_KEY = 'url';

  useEffect(() => {
    const params: Parameters<typeof document.addEventListener> = [
      'click',
      (e) => {
        if (!(e.target instanceof Element && e.target.closest('.al-asset-link-input'))) {
          onClose();
        }
      },
    ];
    document.addEventListener(...params);
    return () => document.removeEventListener(...params);
  }, [onClose]);

  return (
    <Form
      className="al-asset-link-form"
      size="small"
      form={form}
      onFinish={({ url }) => {
        dispatch(assetLinkActions.editSubmitted({ assetId, linkId, url }));
        onClose();
      }}
    >
      <Form.Item
        className="al-asset-link-input"
        rules={[
          { type: 'url', message: '' },
          { required: true, message: '' },
        ]}
        initialValue={initialValue}
        name={FIELD_KEY}
      >
        <Input
          autoFocus
          size="small"
          onChange={(e) => {
            const newValue = e.target.value;
            if (newValue !== newValue.trim()) {
              form.setFieldValue(FIELD_KEY, newValue.trim());
              void form.validateFields([FIELD_KEY]);
            }
          }}
          suffix={
            <TickCircle onClick={() => form.submit()} className="al-asset-link-input__submit" />
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              form.submit();
            } else if (e.key === 'Escape') {
              onClose();
            }
          }}
        />
      </Form.Item>
    </Form>
  );
};
